// RoleManagement.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { getApiUrl } from "../config/api";
import { Container } from '@/Components/ui/container';
import { Card, CardContent, CardHeader, CardTitle } from '@/Components/ui/card';
import { Input } from '@/Components/ui/input';
import { Button } from '@/Components/ui/button';
import { Heading, Text } from '@/Components/ui/typography';
import { Stack, Inline } from '@/Components/ui/layout';
import { Label } from '@/Components/ui/label';
import { Badge } from '@/Components/ui/badge';

const RoleManagementDashboard = () => {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const roles = ["real_user", "admin", "tester"];

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(getApiUrl("/dashboard"), {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          withCredentials: true,
      });
      setUsers(response.data);
    } catch (error) {
      setMessage({ type: "error", text: "Failed to fetch users" });
    }
  };

  const updateRole = async () => {
    if (!email || !role) {
      setMessage({ type: "error", text: "Please provide both email and role" });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        getApiUrl("/dashboard"),
        { email, role },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      setMessage({ type: "success", text: "Role updated successfully" });
      fetchUsers(); // Refresh user list
      setEmail("");
      setRole("");
    } catch (error) {
      setMessage({ 
        type: "error", 
        text: error.response?.data?.message || "Failed to update role" 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="p-8 max-w-[1200px] mx-auto">
      <Heading level="h1" size="3xl" className="mb-8">
        Role Management Dashboard
      </Heading>
      
      {/* Role Update Form */}
      <Card className="mb-8">
        <CardContent className="p-4">
          <Inline spacing="md" className="flex gap-4 mb-4 p-4 bg-white/5 rounded-lg">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="User email"
              className="flex-1 py-2 px-2 rounded border border-slate-700 bg-transparent text-white"
            />
            
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="flex-1 py-2 px-2 rounded border border-slate-700 bg-transparent text-white"
            >
              <option value="">Select Role</option>
              {roles.map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
            
            <Button 
              onClick={updateRole}
              disabled={loading}
              className="py-2 px-4 bg-gradient-to-r from-green-600 via-green-400 to-green-600 border-none rounded text-white cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {loading ? "Updating..." : "Update Role"}
            </Button>
          </Inline>
        </CardContent>
      </Card>

      {/* Message Display */}
      {message.text && (
        <div className={`p-4 rounded mb-4 ${
          message.type === 'success' 
            ? 'bg-green-500/10 text-green-400' 
            : 'bg-red-500/10 text-red-400'
        }`}>
          {message.text}
        </div>
      )}

      {/* Users List */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>
            <Heading level="h2" size="2xl">Users</Heading>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="w-full overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-white/5">
                  <th className="p-4 text-left border-b border-slate-700">Email</th>
                  <th className="p-4 text-left border-b border-slate-700">Current Role</th>
                  <th className="p-4 text-left border-b border-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id} className="border-b border-slate-700">
                    <td className="p-4">{user.email}</td>
                    <td className="p-4">
                      <Badge variant="outline">{user.role}</Badge>
                    </td>
                    <td className="p-4">
                      <select
                        value=""
                        onChange={(e) => {
                          setEmail(user.email);
                          setRole(e.target.value);
                        }}
                        className="py-1 px-2 rounded border border-slate-700 bg-transparent text-white"
                      >
                        <option value="">Change Role</option>
                        {roles.map(role => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </Container>
  );
};

export default RoleManagementDashboard;